import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import { Eye, EyeOff, UserPlus, Sparkles, Heart, GraduationCap, Briefcase, HandHeart, HelpCircle } from 'lucide-react';

const tagOptions = [
  { key: 'autistic', label: 'I am autistic', icon: Sparkles, color: 'border-[hsl(195,77%,45%)] bg-[hsl(195,77%,95%)]' },
  { key: 'parent', label: 'I am a parent of an autistic person', icon: Heart, color: 'border-[hsl(166,56%,45%)] bg-[hsl(166,56%,95%)]' },
  { key: 'teacher', label: 'I am a teacher or educator', icon: GraduationCap, color: 'border-[hsl(54,100%,45%)] bg-[hsl(54,100%,95%)]' },
  { key: 'employer', label: 'I am an employer', icon: Briefcase, color: 'border-[hsl(27,100%,55%)] bg-[hsl(27,100%,95%)]' },
  { key: 'volunteer', label: 'I want to volunteer', icon: HandHeart, color: 'border-[hsl(346,100%,65%)] bg-[hsl(346,100%,95%)]' },
  { key: 'suspect_autistic', label: 'I suspect I might be autistic', icon: HelpCircle, color: 'border-[hsl(320,68%,51%)] bg-[hsl(320,68%,95%)]' }
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    tags: {
      autistic: false,
      parent: false,
      teacher: false,
      employer: false,
      volunteer: false,
      suspect_autistic: false
    },
    newsletter: false,
    accept_terms: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTagChange = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: {
        ...prev.tags,
        [tag]: !prev.tags[tag]
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!formData.accept_terms) {
      setError('You must accept the terms and conditions');
      return;
    }

    // Check if at least one tag is selected
    const hasTag = Object.values(formData.tags).some(v => v);
    if (!hasTag) {
      setError('Please select at least one option that describes you');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...registerData } = formData;
      const user = await register(registerData);
      toast.success(`Welcome to Autism Acceptance, ${user.first_name}!`);
      
      // If they selected volunteer, redirect to volunteer form
      if (formData.tags.volunteer) {
        navigate('/volunteer');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card className="card-base">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">Join the Hub</CardTitle>
            <CardDescription>
              Create your account to access resources, connect with the community, and stay informed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive" data-testid="register-error">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Start Here - Pathway Selection */}
              <div className="space-y-3 p-4 bg-muted/30 rounded-xl">
                <Label className="text-lg font-semibold">Start Here: I identify as... (select all that apply) *</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  This helps us personalise your experience and show you the most relevant resources.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {tagOptions.map((option) => (
                    <label
                      key={option.key}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.tags[option.key] 
                          ? option.color
                          : 'border-border hover:border-primary/50 bg-background'
                      }`}
                      data-testid={`tag-${option.key}`}
                    >
                      <Checkbox
                        checked={formData.tags[option.key]}
                        onCheckedChange={() => handleTagChange(option.key)}
                      />
                      <option.icon className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Personal Info */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                    data-testid="register-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    data-testid="register-last-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="register-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  data-testid="register-phone"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={8}
                      data-testid="register-password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    data-testid="register-confirm-password"
                  />
                </div>
              </div>

              {/* Newsletter */}
              <div className="flex items-center gap-3">
                <Checkbox
                  id="newsletter"
                  checked={formData.newsletter}
                  onCheckedChange={(checked) => setFormData({ ...formData, newsletter: checked })}
                  data-testid="register-newsletter"
                />
                <Label htmlFor="newsletter" className="cursor-pointer">
                  Subscribe to our monthly newsletter
                </Label>
              </div>

              {/* Terms */}
              <div className="space-y-2 p-4 bg-muted/50 rounded-xl">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms"
                    checked={formData.accept_terms}
                    onCheckedChange={(checked) => setFormData({ ...formData, accept_terms: checked })}
                    className="mt-1"
                    data-testid="register-terms"
                  />
                  <Label htmlFor="terms" className="cursor-pointer text-sm">
                    I accept the <Link to="/terms" className="text-primary underline">Terms & Conditions</Link> and 
                    understand that:
                    <ul className="mt-2 ml-4 list-disc text-muted-foreground space-y-1">
                      <li>My data will be stored securely according to data protection laws</li>
                      <li>Information provided on this hub is for educational purposes and has been sourced from various sources</li>
                      <li>Autism Acceptance is not liable for any actions taken based on information provided</li>
                      <li>Materials are copyright Autism Acceptance 2026 but may be used for distribution purposes</li>
                    </ul>
                  </Label>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full btn-primary" 
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? 'Creating account...' : (
                  <>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Create Account
                  </>
                )}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-medium hover:underline">
                Sign In
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
